import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import FormErrorMessage from '@/app/common/components/FormErrorMessage'
import { cn, currencyFormatter } from '@/app/common/utilities'
import { createPurseFormSchema } from '@/app/pages/providers/pages/choose_purses/choose_purses.schema'
import { Input } from '@/common/components/Input'
import { PaymentType } from '@/schemas/payments.schema'
import { PursesArray } from '@/schemas/provider.schema'
import { Controller, useFieldArray, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { Button } from '../Button'
import { roundToTwoDecimals } from './purse_allocation.utils'
import { PurseInput } from './purse_input'
import { ApplicantPaymentAmount } from '../../payment.utils'

export type PurseFormData = z.infer<ReturnType<typeof createPurseFormSchema>>

interface PurseAllocationFormProps {
  apiPurses: PursesArray
  paymentAmount: ApplicantPaymentAmount
  methods: UseFormReturn<PurseFormData>
  paymentType: PaymentType
  allowedPurseIds?: number[]
}

export const PurseAllocationForm = ({
  apiPurses,
  paymentAmount,
  paymentType,
  methods,
  allowedPurseIds,
}: PurseAllocationFormProps) => {
  const { t: translate } = useTranslation()
  const navigate = useNavigate()

  const {
    control,
    watch,
    trigger,
    getValues,
    setValue,
    formState: { errors },
  } = methods
  const { t } = useTranslation()

  const { fields } = useFieldArray({
    control,
    name: 'purses',
  })

  // Watch for changes in the purses selection
  const purseSelections = watch('purses')

  // Modify the distributePurseAmounts function to respect manually edited amounts
  const distributePurseAmounts = (index: number, isSelected: boolean) => {
    const updatedSelections = [...purseSelections]
    updatedSelections[index] = { ...updatedSelections[index], isSelected }

    // Filter to get only selected purses after the current change
    const selectedPurses = updatedSelections.filter((purse) => purse.isSelected)
    const selectedCount = selectedPurses.length

    if (selectedCount > 0) {
      // Find corresponding API purses for selected purses to get available balances
      const selectedPursesWithDetails = selectedPurses.map((purse) => {
        const apiPurse = apiPurses.find((p) => p.id === purse.purse_id)
        if (!apiPurse) {
          throw new Error(`Wallet with ID ${purse.purse_id} not found`)
        }
        return {
          ...purse,
          index: updatedSelections.findIndex(
            (p) => p.purse_id === purse.purse_id
          ),
          availableBalance: apiPurse.balance_available,
        }
      })

      // Initialize all unselected purses with zero
      updatedSelections.forEach((purse, idx) => {
        if (!purse.isSelected) {
          setValue(`purses.${idx}.amount`, 0)
        }
      })

      // Separate manually edited purses from auto-distributed ones
      const manuallyEditedPurses = selectedPursesWithDetails.filter(
        (purse) =>
          getValues(`purses.${purse.index}.hasBeenManuallyEdited`) === true
      )

      const autoDistributedPurses = selectedPursesWithDetails.filter(
        (purse) =>
          getValues(`purses.${purse.index}.hasBeenManuallyEdited`) !== true
      )

      // Calculate total amount already allocated to manually edited purses
      let manuallyAllocatedAmount = 0
      manuallyEditedPurses.forEach((purse) => {
        manuallyAllocatedAmount +=
          getValues(`purses.${purse.index}.amount`) || 0
      })

      // Calculate remaining amount to distribute among auto-distributed purses
      const remainingAmount = Math.max(
        0,
        paymentAmount - manuallyAllocatedAmount
      )

      if (autoDistributedPurses.length > 0 && remainingAmount > 0) {
        // Convert to cents for precise calculation
        const remainingCents = Math.round(remainingAmount * 100)
        const baseCentsPerPurse = Math.floor(
          remainingCents / autoDistributedPurses.length
        )
        let extraCents =
          remainingCents - baseCentsPerPurse * autoDistributedPurses.length

        // First pass: Distribute base amount to all auto-distributed purses
        autoDistributedPurses.forEach((purse) => {
          // Start with the base amount in cents
          let purseAmountCents = baseCentsPerPurse

          // If we have remaining cents, add one to this purse
          if (extraCents > 0) {
            purseAmountCents += 1
            extraCents -= 1
          }

          // Convert cents back to dollars
          const purseAmount = purseAmountCents / 100

          // Check against available balance
          const allocatableAmount = Math.min(
            purseAmount,
            purse.availableBalance
          )

          setValue(`purses.${purse.index}.amount`, allocatableAmount)
        })

        // If there's still amount remaining due to balance constraints, redistribute
        let totalAllocated = 0

        // Sum up all allocated amounts (both manual and auto)
        selectedPursesWithDetails.forEach((purse) => {
          totalAllocated += getValues(`purses.${purse.index}.amount`) || 0
        })

        let redistributeAmount = roundToTwoDecimals(
          paymentAmount - totalAllocated
        )

        if (redistributeAmount > 0) {
          // Get auto-distributed purses that still have capacity
          const pursesWithCapacity = autoDistributedPurses.filter((purse) => {
            const currentAmount = getValues(`purses.${purse.index}.amount`) || 0
            return purse.availableBalance > currentAmount
          })

          // If we have purses with remaining capacity
          if (pursesWithCapacity.length > 0) {
            // Sort purses by available capacity (descending)
            pursesWithCapacity.sort((a, b) => {
              const aCurrentAmount = getValues(`purses.${a.index}.amount`) || 0
              const bCurrentAmount = getValues(`purses.${b.index}.amount`) || 0
              return (
                b.availableBalance -
                bCurrentAmount -
                (a.availableBalance - aCurrentAmount)
              )
            })

            // Keep allocating until no remaining amount or no capacity left
            let remainingToDistribute = redistributeAmount
            let i = 0

            // Add a safety counter to prevent infinite loops
            let iterationCount = 0
            const maxIterations = pursesWithCapacity.length * 2 // Reasonable upper limit

            while (
              remainingToDistribute > 0 &&
              i < pursesWithCapacity.length &&
              iterationCount < maxIterations
            ) {
              const purse = pursesWithCapacity[i]
              const currentAmount =
                getValues(`purses.${purse.index}.amount`) || 0
              const remainingCapacity = roundToTwoDecimals(
                purse.availableBalance - currentAmount
              )

              // Allocate the minimum of what's left to distribute and the purse's remaining capacity
              const additionalAmount = Math.min(
                remainingToDistribute,
                remainingCapacity
              )

              // Only proceed if we can actually add something meaningful
              if (additionalAmount > 0) {
                const newAmount = roundToTwoDecimals(
                  currentAmount + additionalAmount
                )

                setValue(`purses.${purse.index}.amount`, newAmount)
                remainingToDistribute = roundToTwoDecimals(
                  remainingToDistribute - additionalAmount
                )
              }

              i++
              iterationCount++

              // If we've gone through all purses but still have amount to distribute, start over
              if (
                i === pursesWithCapacity.length &&
                remainingToDistribute > 0
              ) {
                // Check if we can't distribute any more due to precision/rounding issues
                if (remainingToDistribute < 0.01) {
                  // Minimal amount we can work with (1 cent)
                  // Handle small leftover amount - either add to the first purse or discard
                  if (pursesWithCapacity.length > 0) {
                    const firstPurseIndex = pursesWithCapacity[0].index
                    const firstPurseAmount =
                      getValues(`purses.${firstPurseIndex}.amount`) || 0
                    setValue(
                      `purses.${firstPurseIndex}.amount`,
                      roundToTwoDecimals(
                        firstPurseAmount + remainingToDistribute
                      )
                    )
                  }
                  break // Exit the loop
                }

                i = 0
              }
            }

            // If we still have a remaining amount due to capacity constraints or precision issues
            if (remainingToDistribute > 0) {
              console.warn(
                `Could not distribute full amount. Remaining: ${remainingToDistribute}`
              )
              // Additional handling could be added here
            }
          }
        }
      }

      trigger('purses')
    }
  }

  if (apiPurses.length === 0) {
    return (
      <div className="max-w-2xl mx-auto mt-8">
        <Card className="w-full">
          <CardContent className="py-12 px-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-amber-100 mb-4">
                <svg
                  className="h-6 w-6 text-amber-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {translate('no_purses_found')}
              </h3>
              <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto">
                We were unable to find any wallets available for allocation.
                Please check back later or contact support if you believe this
                is an error.
              </p>
              <Button
                onClick={() => {
                  navigate(-1)
                }}
                className="min-w-[120px]"
              >
                {translate('back')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4 w-full">
      <Card className="w-full">
        <CardHeader className="flex items-center justify-between font-medium ">
          <div>{t('your_total_is')}</div>
          <div>{currencyFormatter.format(paymentAmount)}</div>
        </CardHeader>
        <CardContent className="w-full">
          {fields.map((field, index) => {
            const purse = apiPurses.find((p) => {
              return p.id === field.purse_id
            })

            if (!purse) {
              throw new Error('Wallet not found')
            }

            const isRestrictedReimbursementWallet =
              paymentType === 'Reimbursement' && purse.reimbursement_restricted
            const isRestrictredPurse =
              typeof allowedPurseIds !== 'undefined' &&
              !allowedPurseIds?.includes(purse.id)
            const isDisabled =
              purse.balance_available <= 0 ||
              isRestrictedReimbursementWallet ||
              isRestrictredPurse
            return (
              <div
                key={field.id}
                className="py-6 border-b border-gray-200 h-full"
              >
                <div className="w-full ">
                  <div className="w-full">
                    <div
                      className={cn(`relative flex items-start w-fulll`, {
                        'opacity-50': isDisabled,
                        'cursor-not-allowed': isDisabled,
                      })}
                    >
                      <div className="flex h-6 items-center">
                        <Controller
                          name={`purses.${index}.isSelected`}
                          control={control}
                          render={({ field }) => (
                            <Input
                              disabled={isDisabled}
                              id={`purse-${purse.id}`}
                              type="checkbox"
                              className="disabled:opacity-50 flex-shrink-0 flex-grow"
                              checked={field.value}
                              onBlur={() => {
                                field.onBlur()
                                trigger('purses')
                              }}
                              onChange={(e) => {
                                const isChecked = e.target.checked

                                if (!isChecked) {
                                  setValue(
                                    `purses.${index}.hasBeenManuallyEdited`,
                                    false
                                  )
                                }
                                field.onChange(isChecked)
                                distributePurseAmounts(index, isChecked)
                              }}
                              aria-describedby="purse-description"
                            />
                          )}
                        />
                      </div>
                      <div className="text-left ml-3 flex items-center justify-between w-full">
                        <div>
                          <label
                            className="leading-6 font-medium text-gray-800"
                            htmlFor={`purse-${purse.id}`}
                          >
                            {purse.name}
                          </label>
                          <div className="text-sm text-gray-600">
                            {purse.student_name}
                          </div>
                        </div>
                        <div className="font-medium text-sm">
                          <span className="text-emerald-700  ">
                            {currencyFormatter.format(purse.balance_available)}{' '}
                          </span>
                          {translate('available')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {watch(`purses.${index}.isSelected`) && (
                  <div className="mt-4">
                    <PurseInput
                      onFocus={() => {
                        setValue(`purses.${index}.hasBeenManuallyEdited`, true)
                      }}
                      control={control}
                      name={`purses.${index}.amount`}
                      paymentAmount={paymentAmount}
                      errorMessage={errors.purses?.[index]?.amount?.message}
                    />
                  </div>
                )}
              </div>
            )
          })}

          {errors.purses && !Array.isArray(errors.purses) && (
            <FormErrorMessage
              message={errors.purses.message}
              className="mt-4"
            />
          )}
          {errors?.purses?.root?.message && (
            <FormErrorMessage
              message={t(`${errors?.purses.root?.message.trim()}`)}
              className="mt-4"
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
